/**
 * Per-currency money lists — `[{ currency, amount }]`, one entry per currency,
 * as the global Overview routes send them (api#1116, api#1121).
 *
 * NEVER SUMMED ACROSS CURRENCIES. Apex Directional TX bills USD and AB bills
 * CAD; one figure over both is a number nobody billed. A list renders as its
 * entries side by side.
 */
import { fmtCurrency } from './display-labels';

export interface CurrencyAmount {
  currency: string;
  amount: number;
}

/**
 * One currency's figure. fmtCurrency spells CAD and USD; any other code is
 * written out rather than shown behind a bare "$" that would claim one of them.
 */
export function fmtAmount(a: CurrencyAmount): string {
  return fmtAmountDigits(a, 2);
}

/**
 * The same, to a chosen number of decimals (the pipeline draws whole dollars).
 * A separate function, not a second parameter on fmtAmount: callers pass
 * fmtAmount straight to `.map`, which would hand it the INDEX as the digits.
 */
export function fmtAmountDigits(a: CurrencyAmount, digits: number): string {
  if (a.currency === 'CAD' || a.currency === 'USD') return fmtCurrency(a.amount, a.currency, digits);
  const n = fmtCurrency(a.amount, null, digits);
  return n === '—' ? n : `${n.replace('$', '')} ${a.currency}`;
}

/**
 * "US$7,175.00 · CA$1,200.00". An empty list is `empty` — the caller decides
 * what none means at its site (a real $0.00 on a ledger, — when unknown).
 */
export function fmtAmounts(list: CurrencyAmount[] | null | undefined, empty: string): string {
  if (!list || list.length === 0) return empty;
  return list.map(fmtAmount).join(' · ');
}
